import { Box, useTheme } from '@ltvco/refresh-lib/theme';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import { useEffect, useState } from 'react';

import { CHATBOT_INTERACTION_TYPES } from 'routes/SearchPerson/SearchFilter/constants';

import { useSearchPalConversation } from './hooks/useSearchPalConversation';
import { SearchpalChatbot } from './SearchpalChatbot';
import { SearchpalChatbotHeader } from './SearchpalChatbotHeader';
import { SearchpalChatbotInput } from './SearchpalChatbotInput';
import { ROLES, MESSAGES_SECTION_TYPES } from './utils/constants';
import { getCurrentTime } from './utils/conversationUtils';

export interface SearchpalChatbotPersistentViewProps {
  showNotification?: boolean;
  notificationContent?: string;
  onNotificationClose?: () => void;
  triggerNotification?: boolean;
  // eslint-disable-next-line no-unused-vars
  setInitialNotificationVisible: (visible: boolean) => void;
  resultsCount?: number;
  isOpen: boolean;
  // eslint-disable-next-line no-unused-vars
  setChatbotOpen: (type: string) => void;
  isDrawer?: boolean;
}

export function SearchpalChatbotPersistentView({
  resultsCount,
  setInitialNotificationVisible,
  isOpen,
  setChatbotOpen,
  isDrawer,
}: SearchpalChatbotPersistentViewProps) {
  const [inputValue, setInputValue] = useState('');

  const {
    isGeneratingResponse,
    addMessage,
    handleUserMessage,
    messages,
    messagesEndRef,
    applyPendingFilters,
    handleReset,
    initialMessageSent,
  } = useSearchPalConversation();

  useEffect(() => {
    if (initialMessageSent && !isOpen) {
      setInitialNotificationVisible(true);
    }
  }, [initialMessageSent]);

  const theme = useTheme();
  const { isDesktop, isMedium, isMobile } = useScreenSize();
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && inputValue.trim()) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim()) {
      return;
    }
    const text = inputValue;
    addMessage({
      blocks: [{ type: MESSAGES_SECTION_TYPES.plain, text }],
      role: ROLES.user,
      shouldShowConfirmChip: false,
      timestamp: getCurrentTime(),
    });
    setInputValue('');
    await handleUserMessage(text);
  };
  return (
    <>
      <Box
        sx={() => ({
          display: 'flex',
          flexDirection: 'column',
          borderRadius: !isDesktop ? 0 : '16px',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: '0 0 1px -2px rgba(0, 0, 0, 0.2)',
          p: 0,
          m: 0,
          position: 'relative',
          boxSizing: 'border-box',
          height: isDesktop ? '97%' : '100%',
          minWidth: (isMedium || isMobile) && !isDrawer ? '100vw' : 'unset',
          marginRight: isDesktop ? 1 : 0,
        })}
      >
        <SearchpalChatbotHeader
          onClose={() => setChatbotOpen(CHATBOT_INTERACTION_TYPES.CLOSE)}
        />
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 1,
              height: '100%',
              width: '100%',
              position: 'relative',
              backgroundColor: theme.palette.background.default,
            }}
          >
            <SearchpalChatbot
              expandContent={true}
              resultsCount={resultsCount}
              messages={messages}
              isGeneratingResponse={isGeneratingResponse}
              messagesEndRef={messagesEndRef}
              applyPendingFilters={applyPendingFilters}
              setChatbotOpen={setChatbotOpen}
              isDrawer={isDrawer}
            />
          </Box>
        </Box>
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            zIndex: 2,
            px: 2,
            boxShadow: '0 -2px 8px rgba(0,0,0,0.04)',
            borderRadius: '0 0 16px 16px',
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <SearchpalChatbotInput
            inputValue={inputValue}
            handleInputChange={handleInputChange}
            handleKeyDown={handleKeyDown}
            disabledConditions={isGeneratingResponse}
            onSend={handleSendMessage}
            handleReset={handleReset}
          />
        </Box>
      </Box>
    </>
  );
}
